import React, { useEffect, useRef, useCallback, useState } from 'react';
import styled from 'styled-components';
import colors from '../theme/colors';
import Icon from '../Donee/components/common/Icon';
import { logError, logInfo } from '../../utils/logError';
import { FacebookAuthResponse } from '../../api';

declare global {
    interface Window {
        fbAsyncInit: () => void;
        FB: any;
    }
}

const StyledButton = styled.button`
    font-size: 16px;
    height: 52px;
    width: 100%;
    border-radius: 100px;
    display: flex;
    padding-left: 12px;
    padding-right: 12px;
    align-items: center;
    justify-content: space-between;
    cursor: pointer;
    color: ${colors.white};
    background-color: ${colors.externalFacebookColor};
    border: none;
    outline: none;

    .facebook-icon {
        margin-right: 10px;
    }

    .hide {
        visibility: hidden;
    }
`;

export interface FacebookLoginResponse {
    status: undefined;
    id: string;
    userID: string;
    accessToken: string;
    name?: string;
    email?: string;
    picture?: {
        data: {
            height?: number;
            is_silhouette?: boolean;
            url?: string;
            width?: number;
        };
    };
}

interface FacebookLoginButtonProps {
    appId: string;
    onSuccess: (response: FacebookAuthResponse) => void;
    onFailure?: (response: { status: string }) => void;
    className?: string;
    fields?: string;
    onClick?: () => void;
    isDisabled?: boolean;
    text?: string;
}

const fbSDKVersion = 'v18.0';
const getFacebookSDKUrl = (appId: string) => {
    return `https://connect.facebook.net/en_US/sdk.js#xfbml=1&version=${fbSDKVersion}&appId=${appId}&autoLogAppEvents=1`;
};
const FacebookLoginButton: React.FC<FacebookLoginButtonProps> = ({
    appId,
    onSuccess,
    onFailure,
    className,
    fields = 'id,first_name,last_name,name,email,gender,locale,picture',
    onClick,
    isDisabled = false,
    text = 'Continue with Facebook',
}) => {
    const isProcessing = useRef<boolean>(false);
    const [isSdkLoaded, setIsSdkLoaded] = useState<boolean>(false);

    const initFacebookSDK = useCallback(() => {
        if (!window.FB) {
            logError('Facebook SDK not loaded yet');
            return;
        }

        window.FB.init({
            appId: appId,
            cookie: true,
            xfbml: true,
            version: fbSDKVersion,
            autoLogAppEvents: true,
        });

        // Check login status
        window.FB.getLoginStatus((response: any) => {
            logInfo('Facebook login status:', response);
        });

        setIsSdkLoaded(true);
    }, [appId]);

    const handleClick = useCallback(() => {
        if (isDisabled || isProcessing.current) return;
        onClick?.();

        if (!window.FB) {
            if (onFailure) {
                onFailure({ status: 'facebookNotLoaded' });
            }
            return;
        }

        isProcessing.current = true;

        window.FB.login(
            (response: any) => {
                isProcessing.current = false;

                if (response.status === 'connected') {
                    window.FB.api('/me', { fields }, (profileResponse: any) => {
                        const authResponse = response.authResponse;
                        const userInfo: FacebookAuthResponse = {
                            status: undefined,
                            id: profileResponse.id,
                            userID: profileResponse.id,
                            accessToken: authResponse.accessToken,
                            name: profileResponse.name,
                            email: profileResponse.email,
                            picture: profileResponse.picture
                                ? { data: profileResponse.picture.data }
                                : undefined,
                        };
                        logInfo('Facebook login successful', userInfo);
                        onSuccess(userInfo);
                    });
                } else {
                    logInfo('Facebook login cancelled');
                    onFailure?.({ status: 'user_cancelled' });
                }
            },
            {
                scope: 'public_profile,email',
                return_scopes: true,
                auth_type: 'rerequest',
            },
        );
    }, [fields, isDisabled, onFailure, onClick, onSuccess]);

    useEffect(() => {
        // Check if SDK is already loaded
        if (window.FB) {
            initFacebookSDK();
            return;
        }

        // Check if script is already added
        if (document.getElementById('facebook-jssdk')) {
            window.fbAsyncInit = initFacebookSDK;
            return;
        }

        // Initialize the Facebook SDK
        window.fbAsyncInit = initFacebookSDK;

        // Load the Facebook SDK script
        const script = document.createElement('script');
        script.id = 'facebook-jssdk';
        script.async = true;
        script.defer = true;
        script.crossOrigin = 'anonymous';
        script.src = getFacebookSDKUrl(appId);

        // Insert the script in the document
        const firstScript = document.getElementsByTagName('script')[0];
        firstScript.parentNode?.insertBefore(script, firstScript);

        return () => {
            // Clean up
            document.getElementById('facebook-jssdk')?.remove();
            // @ts-ignore - FB is a global variable added by Facebook SDK
            delete window.fbAsyncInit;
            // @ts-ignore - FB is a global variable added by Facebook SDK
            delete window.FB;
        };
    }, [appId, initFacebookSDK]);

    return (
        <StyledButton
            className={className}
            onClick={handleClick}
            disabled={isDisabled || !isSdkLoaded}
            type="button"
        >
            <Icon
                fluid={false}
                name={'facebook-white-circle'}
                size={'medium'}
                width={'link'}
                raised={'none'}
                color={'transparent'}
                iconProps={undefined}
                className={'facebook-icon'}
            />
            <div>{text}</div>
            <Icon
                fluid={false}
                name={'facebook-white-circle'}
                size={'medium'}
                width={'link'}
                raised={'none'}
                color={'transparent'}
                iconProps={undefined}
                className={'facebook-icon hide'}
            />
        </StyledButton>
    );
};

export default FacebookLoginButton;
